import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, KeyRound, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <main className="min-h-screen bg-charcoal text-parchment flex items-center justify-center px-6 grain relative" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
        className="glass rounded-3xl p-10 max-w-md w-full">
        {!token && (
          <div className="text-center">
            <XCircle className="w-12 h-12 text-terracotta mx-auto" />
            <h1 className="mt-6 font-display font-bold text-2xl" data-testid="reset-invalid-headline">Invalid reset link</h1>
            <p className="mt-3 text-sm text-mist">This link is missing its token. Request a fresh one from the sign-in dialog.</p>
          </div>
        )}
        {token && !done && (
          <>
            <div className="text-center mb-8">
              <KeyRound className="w-10 h-10 text-terracotta mx-auto" />
              <h1 className="mt-5 font-display font-bold text-2xl">Choose a new password</h1>
              <p className="mt-2 text-sm text-mist">At least 8 characters. This link works once and expires in 1 hour.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="rp-password" className="text-xs text-mist">New password</Label>
                <Input id="rp-password" type="password" data-testid="reset-password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" minLength={8} required
                  className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" />
              </div>
              <div>
                <Label htmlFor="rp-confirm" className="text-xs text-mist">Confirm password</Label>
                <Input id="rp-confirm" type="password" data-testid="reset-confirm-input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" minLength={8} required
                  className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" />
              </div>
              <button type="submit" data-testid="reset-submit-button" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-parchment hover:text-charcoal text-parchment font-semibold text-sm py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? "Resetting…" : "Reset Password"}
              </button>
            </form>
          </>
        )}
        {done && (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-fern mx-auto" data-testid="reset-success-icon" />
            <h1 className="mt-6 font-display font-bold text-2xl" data-testid="reset-success-headline">Password updated</h1>
            <p className="mt-3 text-sm text-mist">You can sign in with your new password now. Any old sessions were signed out.</p>
          </div>
        )}
        <Link to="/" data-testid="reset-home-link"
          className="mt-8 block text-center text-sm text-mist hover:text-terracotta transition-colors">
          Back to Home
        </Link>
      </motion.div>
    </main>
  );
}
